import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Car,
  Calendar,
  Route,
  UserCog,
  Settings,
  LogOut,
  BarChart3,
  UsersRound,
  ShieldCheck,
  Shield,
  FileCheck,
  ArrowRightLeft,
  Globe,
  Package,
  Wrench,
  Crown
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import appConfig from '../../config/appConfig';

// Menu items with role-based access
const allMenuItems = [
  {
    title: 'Dashboard',
    icon: LayoutDashboard,
    path: '/dashboard',
    roles: ['owner', 'admin', 'manager', 'accountant', 'dispatcher', 'driver', 'staff']
  },
  {
    title: 'Vehicles',
    icon: Car,
    path: '/cars',
    roles: ['owner', 'admin', 'manager', 'dispatcher']
  },
  {
    title: 'Bookings',
    icon: Calendar,
    path: '/bookings',
    roles: ['owner', 'admin', 'manager', 'accountant', 'dispatcher'],
    business: ['rental', 'both']
  },
  {
    title: 'Journeys',
    icon: Route,
    path: '/journeys',
    roles: ['owner', 'manager', 'dispatcher', 'driver'],
    business: ['rental', 'both']
  },
  {
    title: 'One-Way Trips',
    icon: ArrowRightLeft,
    path: '/oneway-trips',
    roles: ['owner', 'admin', 'manager', 'dispatcher', 'driver'],
    business: ['rental', 'both']
  },
  {
    title: 'Team',
    icon: UsersRound,
    path: '/staff',
    roles: ['owner', 'admin']
  },
  {
    title: 'Drivers',
    icon: UserCog,
    path: '/drivers',
    roles: ['owner', 'manager', 'dispatcher'],
    business: ['rental', 'both']
  },
  {
    title: 'Maintenance',
    icon: Wrench,
    path: '/maintenance',
    roles: ['owner', 'admin', 'manager'],
    business: ['rental', 'both']
  },
  {
    title: 'Packages',
    icon: Package,
    path: '/packages',
    roles: ['owner', 'admin', 'manager'],
    business: ['rental', 'both']
  },
  {
    title: 'Reports',
    icon: BarChart3,
    path: '/reports',
    roles: ['owner', 'admin', 'manager', 'accountant']
  },
  {
    title: 'All Users',
    icon: UsersRound,
    path: '/admin/users',
    roles: ['admin']
  },
  {
    title: 'Owner Verification',
    icon: ShieldCheck,
    path: '/admin/owners',
    roles: ['admin']
  },
  {
    title: 'Admin Users',
    icon: Shield,
    path: '/admin/admins',
    roles: ['admin']
  },
  {
    title: 'My Documents',
    icon: FileCheck,
    path: '/my-documents',
    roles: ['owner']
  },
  {
    title: 'Subscription',
    icon: Crown,
    path: '/subscription',
    roles: ['owner']
  },
  {
    title: 'Browse Vehicles',
    icon: Globe,
    path: '/browse',
    roles: ['owner', 'admin']
  },
  {
    title: 'Settings',
    icon: Settings,
    path: '/settings',
    roles: ['owner', 'admin']
  }
];

export const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user, travelsInfo } = useAuth();
  const { settings } = useSettings();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  // Get display name and logo — admin always shows app name, not travels info
  const isAdmin = user?.role === 'admin';
  const displayName = isAdmin ? appConfig.name : (travelsInfo?.name || appConfig.name);
  const displayCity = isAdmin ? 'Admin Panel' : (travelsInfo?.address?.city || 'Management System');
  // Fall back to the user `photo` field where some owners' logos are stored
  const logoUrl = isAdmin ? null : (travelsInfo?.logo?.url || user?.photo?.url);

  // Get sidebar color from settings or use default
  const sidebarColor = settings?.sidebarColor || '#1F2937';
  const primaryColor = settings?.primaryColor || '#3B82F6';

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 h-screen w-64 flex flex-col z-50 transition-transform duration-300 md:translate-x-0",
        isOpen ? "translate-x-0" : "-translate-x-full"
      )}
      style={{ backgroundColor: sidebarColor }}
    >
      {/* Owner's Travels Branding */}
      <Link to="/dashboard" className="block border-b border-white/10 cursor-pointer hover:opacity-90 transition-opacity">
        {logoUrl ? (
          <div className="flex flex-col items-center">
            <img
              src={logoUrl}
              alt={displayName}
              className="max-h-28 w-full object-cover"
            />
            <div className="text-center py-2 px-2 w-full">
              <h1 className="text-sm font-bold text-white leading-tight break-words">{displayName}</h1>
              <p className="text-xs text-gray-400 mt-1">{displayCity}</p>
            </div>
          </div>
        ) : (
          <div className="flex items-center space-x-3 p-4">
            <div className="flex items-center justify-center h-10 w-10 flex-shrink-0">
              {travelsInfo ? (
                <div className="rounded-lg p-2 h-10 w-10 flex items-center justify-center" style={{ backgroundColor: primaryColor }}>
                  <span className="text-white font-bold text-lg">{displayName.charAt(0).toUpperCase()}</span>
                </div>
              ) : (
                <img src={appConfig.logo} alt={appConfig.name} className="h-10 w-10 object-contain rounded-lg" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-white truncate">{displayName}</h1>
              <p className="text-xs text-gray-400 truncate">{displayCity}</p>
            </div>
          </div>
        )}
      </Link>

      {/* Navigation - Filtered by user role + owner's business type */}
      <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
        {allMenuItems
          .filter(item => item.roles.includes(user?.role))
          .filter(item => !item.business || item.business.includes(user?.businessType || 'both'))
          .map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname.startsWith(item.path);

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={cn(
                  "flex items-center space-x-3 px-4 py-3 rounded transition-all",
                  isActive
                    ? "text-white font-medium shadow-md"
                    : "text-gray-300 hover:bg-white/10"
                )}
                style={isActive ? { backgroundColor: primaryColor } : {}}
              >
                <Icon className={cn(
                  "h-5 w-5",
                  isActive ? "text-white" : "text-gray-400"
                )} />
                <span>{item.title}</span>
              </Link>
            );
          })}
      </nav>

      {/* Logout */}
      <div className="p-4 border-t border-white/10">
        <button
          onClick={handleLogout}
          className="flex items-center space-x-3 px-4 py-3 rounded text-gray-300 hover:bg-red-500/20 hover:text-red-300 transition-all w-full"
        >
          <LogOut className="h-5 w-5" />
          <span>Logout</span>
        </button>
      </div>

    </aside>
  );
};

export default Sidebar;
